/**
 * Hook para dados agregados do modal de negócio
 * Busca contadores em uma única query via RPC
 * Reduz ~30 requisições para apenas 1
 * 
 * Versão: 1.0.0
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('DealModalData');

// Interface dos contadores retornados pela RPC
export interface DealModalCounters {
  conversations: number;
  related_deals: number;
  deal_items: number;
  activities: number;
  contracts: number; // v1.1.0: contador de contratos
  tasks: number;     // v1.2.0: contador de tarefas vinculadas
  vehicles: number;  // v1.5.0: contador de veículos vinculados ao negócio
  proposals: number; // v1.6.0: contador de propostas (exceto canceladas)
}

// Interface do retorno completo da RPC
interface DealModalData {
  counters: DealModalCounters;
}

/**
 * Hook para buscar dados agregados do modal de negócio
 * Utiliza RPC unificada para obter todos os contadores em uma única query
 */
export function useDealModalData(dealId: string | null) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['deal-modal-data', dealId],
    queryFn: async (): Promise<DealModalData | null> => {
      if (!dealId || !currentTenant?.id) return null;

      logger.debug('Buscando dados agregados do modal:', dealId);

      // Usa casting pois tipos são regenerados após migration
      const { data, error } = await supabase.rpc('get_deal_modal_data' as never, {
        p_deal_id: dealId,
        p_tenant_hint: currentTenant.id,
      } as never);

      if (error) {
        logger.error('Erro ao buscar dados do modal:', error.message);
        throw error;
      }

      logger.debug('Dados do modal carregados:', data);
      return data as unknown as DealModalData;
    },
    enabled: !!dealId && !!currentTenant?.id,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });
}
